/**
 * Centralized app lifecycle manager for detecting background/resume transitions.
 *
 * On Android (and iOS), the WebView's WebSocket connections die silently when
 * the app is minimized. The Supabase Realtime client doesn't receive close
 * events, so it thinks it's still connected. Timers (setInterval, setTimeout)
 * also freeze while the app is in the background.
 *
 * This module:
 * 1. Detects when the app transitions from background → foreground
 * 2. Force-reconnects the Supabase Realtime WebSocket transport
 * 3. Notifies registered callbacks so individual subscriptions can self-heal
 *
 * Privacy: No data is sent externally. This is purely an on-device lifecycle
 * state machine.
 */

import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { startBackgroundService, stopBackgroundService } from '@/lib/foregroundService';
import { resetSharedAudioContext } from '@/lib/audioContextPool';
import { runHealthCheckNow } from '@/lib/healthMonitor';

type ResumeCallback = () => void;

let wasBackgrounded = false;
let lastBackgroundTime = 0;
/** Set to true when lock callbacks fire (auth teardown in progress) */
let lockInProgress = false;
const resumeCallbacks = new Set<ResumeCallback>();
const lockCallbacks = new Set<() => void>();
let initialized = false;

/**
 * Register a callback to run when the app resumes from background.
 * Returns an unsubscribe function.
 */
export function onAppResume(callback: ResumeCallback): () => void {
  resumeCallbacks.add(callback);
  return () => resumeCallbacks.delete(callback);
}

/**
 * Register a callback to run when the app enters background (for locking).
 * Returns an unsubscribe function.
 */
export function onAppBackground(callback: () => void): () => void {
  lockCallbacks.add(callback);
  return () => lockCallbacks.delete(callback);
}

/**
 * Check if app was recently resumed from background (within the last N ms).
 */
export function wasRecentlyResumed(withinMs = 3000): boolean {
  if (!wasBackgrounded) return false;
  return Date.now() - lastBackgroundTime < withinMs;
}

function handleBackground() {
  wasBackgrounded = true;
  lastBackgroundTime = Date.now();
  lockInProgress = true;
  console.log('[AppLifecycle] App entering background');

  // Notify lock callbacks (used by MainApp to require re-auth)
  lockCallbacks.forEach((cb) => {
    try { cb(); } catch (err) {
      console.error('[AppLifecycle] Lock callback error:', err);
    }
  });
}

/**
 * Force-reconnect the Supabase Realtime transport with retry logic.
 * On Android the WebSocket silently dies in background, so a simple
 * connect() call often isn't enough — we need to fully tear down and
 * rebuild the connection.
 */
async function forceReconnectRealtime(maxRetries = 3): Promise<void> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Tear down the existing (likely dead) transport
      supabase.realtime.disconnect();
      // Brief pause to let the socket fully close
      await new Promise((r) => setTimeout(r, 300 * attempt));
      // Establish a fresh connection
      supabase.realtime.connect();
      console.log(`[AppLifecycle] Realtime reconnected (attempt ${attempt})`);
      return; // success
    } catch (err) {
      console.warn(`[AppLifecycle] Realtime reconnect attempt ${attempt} failed:`, err);
      if (attempt === maxRetries) {
        console.error('[AppLifecycle] All reconnect attempts exhausted');
      }
    }
  }
}

async function handleResume() {
  if (!wasBackgrounded) return;

  // CRITICAL: If the document is still hidden, this is a false-positive resume
  // fired by Android WebView during minimize animation. Abort immediately.
  if (document.hidden) {
    console.log('[AppLifecycle] Ignoring resume while document is still hidden');
    return;
  }

  const duration = Date.now() - lastBackgroundTime;

  // Ignore false-positive resumes that fire within 2.5s of backgrounding.
  if (duration < 2500) {
    console.log(`[AppLifecycle] Ignoring spurious resume after ${duration}ms`);
    return;
  }

  wasBackgrounded = false;
  console.log(`[AppLifecycle] App resumed after ${Math.round(duration / 1000)}s in background`);

  // RACE GUARD: If lock callbacks already fired (auth teardown), skip realtime
  // reconnection — the auth state is being torn down and reconnecting would
  // cause cascading state mutations that crash the WebView.
  if (lockInProgress) {
    console.log('[AppLifecycle] Skipping realtime reconnect — lock/logout in progress');
    lockInProgress = false;
    return;
  }

  // Force disconnect + reconnect with retries
  await forceReconnectRealtime();

  // Reset AudioContext (Android kills it in background)
  try { resetSharedAudioContext(); } catch {}

  // Run proactive health checks immediately on resume
  runHealthCheckNow();

  // Small delay to let the transport stabilise before firing callbacks
  await new Promise((r) => setTimeout(r, 500));

  // Double-check we're still in the foreground before firing callbacks
  if (document.hidden) {
    console.log('[AppLifecycle] App went back to background before callbacks fired');
    wasBackgrounded = true;
    lastBackgroundTime = Date.now();
    return;
  }

  // Notify all registered callbacks (subscriptions, push listener, etc.)
  resumeCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.error('[AppLifecycle] Resume callback error:', err);
    }
  });
}

/**
 * Initialize the lifecycle manager. Call once at app startup (main.tsx).
 */
export function initAppLifecycle(): void {
  if (initialized) return;
  initialized = true;

  // Web: visibilitychange API (works in both browser and WebView)
  document.addEventListener('visibilitychange', () => {
    try {
      if (document.hidden) {
        handleBackground();
      } else {
        setTimeout(() => { try { handleResume(); } catch (err) { console.error('[AppLifecycle] Resume error:', err); } }, 800);
      }
    } catch (err) {
      console.error('[AppLifecycle] Visibility change error:', err);
    }
  });

  // Native: Capacitor App plugin for more reliable lifecycle events on Android/iOS
  if (Capacitor.isNativePlatform()) {
    import('@capacitor/app')
      .then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            // Wait long enough for Android's minimize animation to fully complete.
            // If the user was just swiping away, document.hidden will still be true
            // and handleResume will bail out.
            setTimeout(() => handleResume(), 1000);
          } else {
            handleBackground();
          }
        });

        // Also handle the 'resume' event (iOS-specific, fires after app resumes from bg)
        App.addListener('resume', () => {
          setTimeout(() => handleResume(), 1000);
        });

        console.log('[AppLifecycle] Capacitor lifecycle listeners registered');
      })
      .catch((err) => {
        console.warn('[AppLifecycle] Capacitor App plugin unavailable:', err);
      });
  }

  // Fallback: detect online events — but skip if lock/logout is in progress
  window.addEventListener('online', () => {
    try {
      if (lockInProgress) {
        console.log('[AppLifecycle] Network online but lock in progress — skipping reconnect');
        return;
      }
      console.log('[AppLifecycle] Network came online, nudging realtime');
      try { supabase.realtime.connect(); } catch {}
      resumeCallbacks.forEach((cb) => {
        try { cb(); } catch {}
      });
    } catch (err) {
      console.error('[AppLifecycle] Online handler error:', err);
    }
  });

  console.log('[AppLifecycle] Initialized');
}
